import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  GET_KNOWLEDGE,
  POST_WEBSITE_KNOWLEDGE,
  GET_POLL_STATUS,
  DELETE_KNOWLEDGE,
} from "../api/aiKnowledge.service";

const initialState = {
  knowledge: [],
  lastUploaded: null,
  loading: false,
  error: null,
  pollLoading: false,
  pollError: null,
  pollResult: null,
  pollingJobId: null,
  deleteLoading: false,
  deleteError: null,
  deletingId: null,
  deleteResult: null,
};

export const getKnowledge = createAsyncThunk(
  "aiKnowledge/getKnowledge",
  async (_, thunkAPI) => {
    try {
      const resp = await GET_KNOWLEDGE();
      return resp;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const uploadWebsiteKnowledge = createAsyncThunk(
  "aiKnowledge/uploadWebsiteKnowledge",
  async ({ KnowledgeUrl, agentId, sourceName }, thunkAPI) => {
    try {
      const resp = await POST_WEBSITE_KNOWLEDGE({
        KnowledgeUrl,
        agentId,
        sourceName,
      });
      return resp;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const pollKnowledgeJob = createAsyncThunk(
  "aiKnowledge/pollKnowledgeJob",
  async ({ jobId }, thunkAPI) => {
    try {
      const resp = await GET_POLL_STATUS(jobId);
      return { jobId, data: resp };
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data || err.message || { jobId }
      );
    }
  }
);

export const deleteKnowledge = createAsyncThunk(
  "aiKnowledge/deleteKnowledge",
  async ({ id }, thunkAPI) => {
    try {
      const resp = await DELETE_KNOWLEDGE(id);
      return { id, data: resp };
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data || err.message || { id }
      );
    }
  }
);

const aiKnowledgeSlice = createSlice({
  name: "aiKnowledge",
  initialState,
  reducers: {
    resetKnowledgeError(state) {
      state.error = null;
    },
    resetPollError(state) {
      state.pollError = null;
    },
    resetDeleteError(state) {
      state.deleteError = null;
    },
    clearKnowledgeState(state) {
      state.knowledge = [];
      state.lastUploaded = null;
      state.loading = false;
      state.error = null;
      state.pollLoading = false;
      state.pollError = null;
      state.pollResult = null;
      state.pollingJobId = null;
      state.deleteLoading = false;
      state.deleteError = null;
      state.deletingId = null;
      state.deleteResult = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getKnowledge.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getKnowledge.fulfilled, (state, action) => {
      state.loading = false;
      state.knowledge = action.payload;
    });
    builder.addCase(getKnowledge.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || action.error.message;
    });

    builder.addCase(uploadWebsiteKnowledge.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(uploadWebsiteKnowledge.fulfilled, (state, action) => {
      state.loading = false;
      state.lastUploaded = action.payload;
    });
    builder.addCase(uploadWebsiteKnowledge.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || action.error.message;
    });

    builder.addCase(pollKnowledgeJob.pending, (state, action) => {
      state.pollLoading = true;
      state.pollError = null;
      state.pollingJobId = action.meta?.arg?.jobId || null;
    });
    builder.addCase(pollKnowledgeJob.fulfilled, (state, action) => {
      state.pollLoading = false;
      state.pollResult = action.payload;
      state.pollingJobId = null;
    });
    builder.addCase(pollKnowledgeJob.rejected, (state, action) => {
      state.pollLoading = false;
      state.pollError = action.payload || action.error.message;
      state.pollingJobId = null;
    });

    builder.addCase(deleteKnowledge.pending, (state, action) => {
      state.deleteLoading = true;
      state.deleteError = null;
      state.deletingId = action.meta?.arg?.id || null;
      state.deleteResult = null;
    });
    builder.addCase(deleteKnowledge.fulfilled, (state, action) => {
      state.deleteLoading = false;
      state.deleteResult = action.payload;
      state.deletingId = null;
    });
    builder.addCase(deleteKnowledge.rejected, (state, action) => {
      state.deleteLoading = false;
      state.deleteError = action.payload || action.error.message;
      state.deletingId = null;
    });

  },
});

export const { resetKnowledgeError, resetPollError, resetDeleteError, clearKnowledgeState } =
  aiKnowledgeSlice.actions;
export default aiKnowledgeSlice.reducer;
